import { useMemo } from "react";
import type {
  PayrollTeam,
  PayrollRoster,
  PayrollRosterContract,
} from "@/payroll/roster/types";
import { useRosterUsers } from "@/payroll/roster/use-roster-users";
import {
  contractColumns,
  contractFieldOptions,
  createPayrollRosterDraftMembers,
  createPayrollRosterSuggestionDrafts,
  diffRoster,
  editedMemberIds,
  hasPayrollRosterMemberErrors,
  isGovernedContract,
  membersNeedingReassignment,
  validatePayrollRosterMember,
} from "@/payroll/roster/utils";

const EMPTY_CONTRACT: PayrollRosterContract = {};
const EMPTY_TEAMS: PayrollTeam[] = [];
const EMPTY_EXTERNAL_IDS: string[] = [];

export const useRosterEditorData = (
  roster: PayrollRoster,
  businessId: string,
) => {
  const { userNames } = useRosterUsers(businessId);
  const teams = roster.available_teams ?? EMPTY_TEAMS;
  const contract = roster.contract ?? EMPTY_CONTRACT;
  const governed = isGovernedContract(contract);
  const baseline = roster.baseline;

  const publishedVersion = baseline?.version_number ?? null;
  const publishedAt = baseline?.published_at ?? null;

  const fields = useMemo(
    () => contractColumns(roster.fields, contract),
    [roster.fields, contract],
  );
  const draftMembers = useMemo(
    () => createPayrollRosterDraftMembers(roster.members),
    [roster.members],
  );
  const dismissedExternalIds =
    roster.removed_member_external_ids ?? EMPTY_EXTERNAL_IDS;

  const fieldOptions = useMemo(
    () =>
      Object.fromEntries(
        fields.map((field) => [
          field.key,
          contractFieldOptions(field.key, contract, field.options),
        ]),
      ),
    [fields, contract],
  );

  const hasErrors = useMemo(
    () =>
      draftMembers.some(({ member }) =>
        hasPayrollRosterMemberErrors(
          validatePayrollRosterMember(member, fields, contract),
        ),
      ),
    [draftMembers, fields, contract],
  );

  const needsReassignment = useMemo(
    () =>
      governed
        ? membersNeedingReassignment(
            draftMembers.map(({ member }) => member),
            fields,
            contract,
          )
        : [],
    [governed, draftMembers, fields, contract],
  );

  const flaggedMembers = useMemo(
    () => new Set(needsReassignment),
    [needsReassignment],
  );

  const changes = useMemo(() => {
    const published = baseline?.removed_member_external_ids ?? [];
    const names = new Map<string, string>();
    (roster.missing_user_suggestions ?? []).forEach((suggestion) => {
      if (!names.has(suggestion.service_titan_employee_id)) {
        names.set(
          suggestion.service_titan_employee_id,
          suggestion.display_name,
        );
      }
    });

    return diffRoster(
      draftMembers.map(({ member, isNew }) => ({ member, isNew })),
      baseline?.members ?? [],
      fields,
      {
        added: dismissedExternalIds.filter((id) => !published.includes(id)),
        names,
      },
      userNames,
    );
  }, [
    baseline?.removed_member_external_ids,
    baseline?.members,
    roster.missing_user_suggestions,
    draftMembers,
    fields,
    dismissedExternalIds,
    userNames,
  ]);
  const hasChanges = changes.length > 0;

  const editedIds = useMemo(
    () => editedMemberIds(draftMembers, baseline?.members ?? [], fields),
    [draftMembers, baseline, fields],
  );

  const suggestionDrafts = useMemo(
    () => createPayrollRosterSuggestionDrafts(roster),
    [roster],
  );
  const displayedDrafts = useMemo(
    () => [...draftMembers, ...suggestionDrafts],
    [draftMembers, suggestionDrafts],
  );
  const memberCount = draftMembers.length;
  const suggestionCount = suggestionDrafts.length;
  const departedIds = useMemo(
    () => new Set(roster.departed_member_ids ?? []),
    [roster.departed_member_ids],
  );
  const departedCount = useMemo(
    () =>
      draftMembers.filter(({ member }) =>
        departedIds.has(member.service_titan_employee_id),
      ).length,
    [draftMembers, departedIds],
  );

  return {
    teams,
    contract,
    publishedVersion,
    publishedAt,
    fields,
    draftMembers,
    dismissedExternalIds,
    fieldOptions,
    hasErrors,
    needsReassignment,
    flaggedMembers,
    changes,
    hasChanges,
    editedIds,
    suggestionDrafts,
    displayedDrafts,
    memberCount,
    suggestionCount,
    departedIds,
    departedCount,
  };
};
